import { useState } from 'react';
import { Check } from 'lucide-react';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import FabricStep from './FabricStep';

const PIPING_IMAGE = 'https://static.prod-images.emergentagent.com/jobs/10ee4892-58a5-4497-923f-6081e7061c9d/images/29dabcaf76d3b2c0447dac87f6899863649c45d21d5d746a94ecc23a0c222385.png';

const PIPING_OPTIONS = [
  { value: 'none', label: 'No Piping', price: 0, description: 'Standard cushion without piping trim' },
  { value: 'same', label: 'Add Piping (Same Material)', price: 25, description: 'Piping sewn using the same fabric as your cushion cover' },
  { value: 'different', label: 'Add Piping (Different Material)', price: 55, description: 'Piping sewn using a contrasting or complementary fabric' },
];

const OptionsStep = ({
  pipingOption,
  onPipingChange,
  pipingFabricRange,
  pipingFabricColor,
  onPipingRangeSelect,
  onPipingColorSelect,
  cushionCategory,
  mainFabricRange,
  mainFabricColor,
}) => {
  const [showFabricPicker, setShowFabricPicker] = useState(false);

  const handlePipingChange = (value) => {
    onPipingChange(value);
    if (value === 'different') {
      setShowFabricPicker(true);
      // Pre-select the main fabric as default for piping
      if (!pipingFabricRange && mainFabricRange) {
        onPipingRangeSelect(mainFabricRange);
        onPipingColorSelect(mainFabricColor);
      }
    } else {
      setShowFabricPicker(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="step-options">
      {/* Piping Section */}
      <div>
        <h2 className="text-lg font-semibold text-[#1A1A1A] mb-1">
          Cushion Options
        </h2>
        <p className="text-gray-600 text-sm mb-4">Add optional extras to your cushion.</p>

        {/* Piping Info */}
        <div className="bg-sand/50 rounded-xl overflow-hidden border border-gray-200 mb-4 sm:flex">
          <img
            src={PIPING_IMAGE}
            alt="Cushion piping detail"
            className="w-full sm:w-1/2 h-40 sm:h-auto object-cover"
          />
          <div className="p-4 sm:flex sm:flex-col sm:justify-center">
            <h3 className="font-semibold text-[#1A1A1A] text-sm sm:text-base mb-1">Piping (Welt Cord)</h3>
            <p className="text-xs sm:text-sm text-gray-600">
              Piping is a fabric-covered cord sewn into the seams along the edges of your cushion. It adds a clean, tailored finish that defines the shape and gives a professional upholstered look. Available in matching or contrasting fabric.
            </p>
          </div>
        </div>

        {/* Piping Options */}
        <RadioGroup
          value={pipingOption}
          onValueChange={handlePipingChange}
          className="space-y-2"
          data-testid="piping-options"
        >
          {PIPING_OPTIONS.map((option) => (
            <label
              key={option.value}
              className={`flex items-start p-3 sm:p-4 rounded-xl border-2 cursor-pointer transition-all ${
                pipingOption === option.value
                  ? 'border-bush bg-bush/5'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
              data-testid={`piping-option-${option.value}`}
            >
              <RadioGroupItem value={option.value} className="mt-0.5 mr-3 h-4 w-4 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-2">
                  <span className="font-semibold text-sm sm:text-base text-[#1A1A1A]">{option.label}</span>
                </div>
                <p className="text-xs sm:text-sm text-gray-500 mt-0.5">{option.description}</p>
              </div>
              {pipingOption === option.value && (
                <div className="ml-2 flex-shrink-0">
                  <div className="w-5 h-5 bg-bush rounded-full flex items-center justify-center">
                    <Check className="h-3 w-3 text-white" />
                  </div>
                </div>
              )}
            </label>
          ))}
        </RadioGroup>

        {/* Different Material Fabric Picker */}
        {pipingOption === 'different' && (
          <div className="mt-4 border-2 border-bush/20 rounded-xl p-3 sm:p-4 bg-bush/5">
            <h4 className="font-semibold text-sm sm:text-base text-[#1A1A1A] mb-3">Select Piping Fabric</h4>
            <FabricStep
              selectedRange={pipingFabricRange}
              selectedColor={pipingFabricColor}
              onRangeSelect={onPipingRangeSelect}
              onColorSelect={onPipingColorSelect}
              cushionCategory={cushionCategory}
              defaultExpanded={pipingFabricRange || mainFabricRange}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export { PIPING_OPTIONS };
export default OptionsStep;
